import React, {useCallback, useMemo, useState} from "react";
import Chips, {ChipsContentType, ChipsIconPosition, ChipsTypes} from "../../../../../base/chips/chips";
import {Col} from "reactstrap";
import {OutlinedInput} from "../../../../../base/input/outlined_input";
import {ReactComponent as EnterIcon} from "../../../../../../../assets/images/products/enter.svg";
import ReasonNoteSection from "../../reason-note-section";
import {Divider} from "@mui/material";

const IncreaseProductAvailabilityDialogSerializableSection = ({
                                                                  hideReasonNoteSection,
                                                                  currentAvailability,
                                                                  selectedSerialNos,
                                                                  setSelectedSerialNos,
                                                                  cost,
                                                                  setCost,
                                                                  modifying,
                                                                  formId,
                                                                  submitReasonNote,
                                                              }) => {
    const [adding, setAdding] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [newSerialNo, setNewSerialNo] = useState('');

    const removeSerialNumber = useCallback((serialNo) => {
        setSelectedSerialNos(prevState => prevState.filter(e => e !== serialNo))
    }, [setSelectedSerialNos]);

    /**
     * Adds a serial number to the selected serial numbers of this section.
     *
     * * validates the serial number before adding it in the server.
     * @type {(function(): Promise<void>)|*}
     */
    const addSerialNumber = useCallback(async () => {
        const trimmedSerialNo = newSerialNo?.trim() ?? '';
        if (!trimmedSerialNo.length)
            return;
        const newSerialList = trimmedSerialNo
            .split(',')
            .map(e => e.trim())
            .filter(e => !!e.length)
            .filter(e => !selectedSerialNos.includes(e))
        ;
        setAdding(true);
        setSelectedSerialNos(prevState => [...prevState, ...newSerialList])
        setNewSerialNo('');
        setErrorMessage('');
        setAdding(false);
    }, [newSerialNo, selectedSerialNos, setSelectedSerialNos])

    /**
     * Sets the newSerialNo's text value with the provided value and removes the errorMessage.
     * @type {(function(*): void)|*}
     */
    const onSerialNoChanged = useCallback((value) => {
        setNewSerialNo(value);
        setErrorMessage('');
    }, []);

    /**
     * Adds the serial number if the pressed key is "Enter".
     * @type {(function(*): void)|*}
     */
    const commitAddingSerialNo = useCallback((e) => {
        switch (e.key) {
            case "Enter":
                addSerialNumber().then();
                break;
            default:
                return;
        }
    }, [addSerialNumber]);

    /**
     * Commits the change of the [cost] prop based on the type of the input value.
     * @type {(function({target: {value: *}}): (undefined|*))|*}
     */
    const onCostChangeCommitted = useCallback(({target: {value}}) => {
        if (!value) {
            return;
        }
        const num = Number(value.trim());
        if (Number.isNaN(num)) {
            return setCost('');
        }
        if (num < 0) {
            return setCost('0');
        }
        setCost(num.toString());
    }, [setCost])

    const addedSerialNos = useMemo(() => {
        return selectedSerialNos.map(e => (
            <Chips
                key={e}
                disabled={adding || modifying}
                contentType={ChipsContentType.text}
                iconPosition={ChipsIconPosition.start}
                type={ChipsTypes.remove}
                onIconClick={() => removeSerialNumber(e)}
                content={e}
                className={'mr-2 mb-2'}
            />
        ));
    }, [adding, modifying, removeSerialNumber, selectedSerialNos])

    return (
        <>
            <div className={'d-flex w-100 flex-column'}>
                <div className={'d-flex w-100 titles-section'}>
                    <Col xs={3}>
                        Current availability
                    </Col>
                    <Col xs={1}/>
                    <Col xs={2}>
                        Adding serials
                    </Col>
                    <Col xs={2}>
                        Cost per item
                    </Col>
                    <Col xs={1}/>
                    <Col xs={3}>
                        New Availability
                    </Col>
                </div>
                <div className={'d-flex w-100'}>
                    <Col xs={3}>
                        <div className={'info-block'}>
                            {currentAvailability}
                        </div>
                    </Col>
                    <Col xs={1}>
                        <p className={'math-icon'}>
                            +
                        </p>
                    </Col>
                    <Col xs={2}>
                        <div className={'info-block'}>
                            {selectedSerialNos?.length ?? 0}
                        </div>
                    </Col>
                    <Col xs={2}>
                        <OutlinedInput
                            value={cost}
                            type={'number'}
                            placeholder={'amount...'}
                            onChange={setCost}
                            onBlur={onCostChangeCommitted}
                            inputProps={{min: 0}}
                            disabled={modifying}
                        />
                    </Col>
                    <Col xs={1}>
                        <p className={'math-icon'}>
                            =
                        </p>
                    </Col>
                    <Col xs={3}>
                        <div className={'info-block'}>
                            {(selectedSerialNos?.length ?? 0) + (currentAvailability ?? 0)}
                        </div>
                    </Col>
                </div>
                <div className={'d-flex w-100 flex-wrap align-items-center mt-5 px-3'}>
                    {addedSerialNos}
                    <div className={'d-flex flex-column mb-2 mr-2'}>
                        <OutlinedInput
                            value={newSerialNo}
                            type={'text'}
                            onChange={onSerialNoChanged}
                            disabled={modifying}
                            onKeyUp={commitAddingSerialNo}
                            icon={<EnterIcon/>}
                            onIconClick={addSerialNumber}
                            error={!!errorMessage?.length}
                            placeholder={'Type serial number...'}
                        />
                        {
                            !!errorMessage?.length &&
                            <p className={'feedback'}>
                                {errorMessage}
                            </p>
                        }
                    </div>
                </div>
                {
                    !hideReasonNoteSection &&
                    <>
                        <Divider className='divider mt-4 w-90 mx-auto'/>
                        <div className={'w-90 m-auto px-4'}>
                            <ReasonNoteSection
                                formId={formId}
                                submitting={modifying}
                                onSubmit={submitReasonNote}
                            />
                        </div>
                    </>
                }
            </div>
        </>
    );
}

export default IncreaseProductAvailabilityDialogSerializableSection;
